import { Formik, useField } from 'formik';
import {
  formatCategory,
  formatGranularity,
} from 'views/StaticContent/SoftSkills/SoftSkills.helpers';
import * as yup from 'yup';
import {
  Button,
  FormikSelectFormField,
  FormikTextInputField,
  Modal,
} from '@spotted-zebra-uk/ui-components';
import {
  categorySelectOptions,
  emptyValues,
  getFiltersCount,
  granularitySelectOptions,
} from './FiltersModal.helpers';
import styles from './FiltersModal.module.scss';

import type { SoftSkillFilters, FormValues } from './FiltersModal.helpers';

type FiltersModalProps = {
  filters: SoftSkillFilters;
  onClose: () => void;
  onSubmit: (values: SoftSkillFilters) => void;
};

export const validationSchema = yup.object().shape({
  taxonomy: yup.number().positive(),
});

export const SoftSkillsFilterModal = ({
  onClose,
  filters,
  onSubmit,
}: FiltersModalProps) => {
  const initialValues = {
    category: filters.categoryFilter
      ? {
          value: filters.categoryFilter,
          label: formatCategory(filters.categoryFilter),
        }
      : emptyValues.category,
    granularity: filters.granularityFilter
      ? {
          value: filters.granularityFilter,
          label: formatGranularity(filters.granularityFilter),
        }
      : emptyValues.granularity,
    taxonomy: filters.taxonomyFilter?.toString() || emptyValues.taxonomy,
  };

  const handleSubmit = (formValues: FormValues) => {
    onSubmit({
      categoryFilter: formValues.category.value || null,
      granularityFilter: formValues.granularity.value || null,
      taxonomyFilter: formValues.taxonomy || null,
    });
    onClose();
  };

  const handleReset = () => {
    onSubmit({
      categoryFilter: null,
      granularityFilter: null,
      taxonomyFilter: null,
    });
    onClose();
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      header="Filter"
      className={styles.filterModal}
    >
      <Formik<FormValues>
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ submitForm, values }) => (
          <>
            <FormikSelectFormField
              id="category"
              placeholder="Category"
              options={categorySelectOptions}
              useFormikField={useField}
              label="Category"
            />
            <FormikSelectFormField
              id="granularity"
              placeholder="Granularity"
              options={granularitySelectOptions}
              useFormikField={useField}
              label="Granularity"
            />
            <FormikTextInputField
              id="taxonomy"
              placeholder="Taxonomy"
              useFormikField={useField}
              label="Taxonomy"
              type="number"
            />
            <div className={styles.buttons}>
              <Button variant="tertiary" onClick={handleReset}>
                Reset filter
              </Button>
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={submitForm}
                counter={getFiltersCount(values) || undefined}
              >
                Apply
              </Button>
            </div>
          </>
        )}
      </Formik>
    </Modal>
  );
};
